import { Box, Button, Chip, CircularProgress, Typography } from "@mui/material";
import { useState } from "react";
import { useQuery } from "react-query";
import { AxiosResponse } from "axios";
import MapComponent from "src/components/common/KML";
import ViewImages from "src/components/common/ViewImage";
import { getNewKmlThread, NewKmlThread } from "src/utils/api/newKml";
import { useUserType } from "src/hook/useUserType";
import { dateAndTimeStamp, downloadS3File } from "src/utils/helper";
import AddKmlModal from "src/components/feature/OrderBook/AddKmlModal";

const statusLabel: Record<string, string> = {
  PENDING_REVIEW: "Uploaded",
  UNDER_QUERY: "Under Query",
  APPROVED: "Approved",
};

const NewKmlSection = ({ epid }: { epid: string }) => {
  const { isImplementingOfficer } = useUserType();
  const [modalOpen, setModalOpen] = useState(false);

  const { data, isLoading, refetch } = useQuery(
    ["newKmlThread", epid],
    () => getNewKmlThread(epid),
    { enabled: !!epid, refetchOnMount: true }
  );

  const thread = (data as AxiosResponse)?.data as NewKmlThread | undefined;

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Nothing proposed yet and the user is not an IO → hide the section entirely
  if (!thread?.newKml?.url && !isImplementingOfficer) return null;

  const isLocked = thread?.isLocked ?? false;
  // IO may submit only for the first upload, or when the admin has raised a query.
  const canSubmit =
    !thread?.newKmlStatus || thread.newKmlStatus === "UNDER_QUERY";

  return (
    <Box sx={{ mt: 3 }}>
      {isImplementingOfficer && canSubmit && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1.5 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setModalOpen(true)}
          >
            {thread?.newKml?.url ? "Replace KML" : "Add KML"}
          </Button>
        </Box>
      )}

      {thread?.newKml?.url ? (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
          {/* Approved KML — always shown on the left when uploaded KML exists */}
          <Box sx={{ flex: "1 1 320px", minWidth: 320 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
              Approved KML
            </Typography>
            {thread.originalKml?.url ? (
              <>
                <ViewImages
                  fileUrl={thread.originalKml.url}
                  label={""}
                  onDownload={() =>
                    downloadS3File(
                      thread.originalKml.url!,
                      `kml_original_${epid}.kml`
                    )
                  }
                />
                <MapComponent kmlUrl={thread.originalKml.url} />
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Not available
              </Typography>
            )}
          </Box>

          {/* Uploaded KML — shown on the right */}
          <Box sx={{ flex: "1 1 320px", minWidth: 320 }}>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Uploaded KML
              </Typography>
              {thread.newKmlStatus && (
                <Chip
                  size="small"
                  color={isLocked ? "success" : "warning"}
                  label={
                    statusLabel[thread.newKmlStatus] ?? thread.newKmlStatus
                  }
                />
              )}
            </Box>
            <ViewImages
              fileUrl={thread.newKml.url}
              label={""}
              onDownload={() =>
                downloadS3File(thread.newKml!.url!, `kml_new_${epid}.kml`)
              }
            />
            <MapComponent kmlUrl={thread.newKml.url} />
          </Box>
        </Box>
      ) : thread?.originalKml?.url ? (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
            Approved KML
          </Typography>
          <ViewImages
            fileUrl={thread.originalKml.url}
            label={""}
            onDownload={() =>
              downloadS3File(
                thread.originalKml.url!,
                `kml_original_${epid}.kml`
              )
            }
          />
          <MapComponent kmlUrl={thread.originalKml.url} />
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No KML available
        </Typography>
      )}

      {!!thread?.thread?.length && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
            Comments
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {thread.thread.map((c) => (
              <Box
                key={c.id}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 1.2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {c.authorUserName || c.authorSubType}{" "}
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                    >
                      ({c.authorSubType})
                    </Typography>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {c.createdAt ? dateAndTimeStamp(c.createdAt, "IST") : ""}
                  </Typography>
                </Box>
                <Typography variant="body2">{c.comment}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {modalOpen && (
        <AddKmlModal
          open={modalOpen}
          epid={epid}
          onClose={() => setModalOpen(false)}
          onSuccess={() => refetch()}
        />
      )}
    </Box>
  );
};

export default NewKmlSection;
